// 절차 효과음 렌더러(c019, 순수 TS — WebAudio를 모른다). 레시피(층 목록) + 시드 → 모노 Float32Array.
// 같은 레시피·시드·샘플레이트면 언제나 같은 샘플이 나온다(시험이 고정). 게임은 소리 첫 재생 전에 변형 몇 개를 미리 렌더해
// AudioBuffer 은행에 넣고(engine.ts), 재생 경로는 렌더하지 않는다. 렌더는 초기화 경로라 여기의 배열 할당은 프레임 루프 밖이다.
//
// 층: osc(사인·삼각·톱니·사각 + 위상 FM + 비브라토 + 피치 스윕) / noise(백색·분홍·갈색) / grains(짧은 핑 여러 개, 반짝이 꼬리)
// 층마다: 시작 시각·길이·엔벨로프(선형 어택·유지·지수 감쇠, 어택 곡선)·이득·필터(저역·고역·대역, 주파수 스윕)·포화(tanh).
// 마스터: 작은 되먹임 딜레이(잔향 대신 짧은 공간감, 고리 안 저역 통과) → 포화 → 끝 페이드 → 음량 맞춤(창 RMS 목표, 피크 상한 -1.2dBFS).

/** 파형 */
export const Wave = { Sine: 0, Tri: 1, Saw: 2, Square: 3 } as const;
/** 잡음 색 */
export const Noise = { White: 0, Pink: 1, Brown: 2 } as const;
/** 필터 종류 */
export const Filt = { Lp: 0, Hp: 1, Bp: 2 } as const;

/** 지수 스윕: time초 동안 from → to(그 뒤 to 유지) */
export interface Sweep { readonly from: number; readonly to: number; readonly time: number }
export type Freq = number | Sweep;

export interface Env {
  /** 어택(초, 0 = 즉시) */
  readonly a: number;
  /** 어택 곡선 지수(1 선형, >1 천천히 차오름 — 텔레그래프의 쌓임) */
  readonly ac?: number;
  /** 유지(초) */
  readonly h?: number;
  /** 지수 감쇠 시간 상수(초). 약 6.9배 뒤 -60dB */
  readonly d: number;
}

export interface FilterSpec { readonly type: number; readonly freq: Freq; readonly q: number }

interface LayerBase {
  /** 시작(초) */
  readonly at: number;
  /** 길이(초). 끝 8ms는 자동 페이드 */
  readonly dur: number;
  readonly env: Env;
  readonly gainDb: number;
  readonly filter?: FilterSpec;
  /** tanh 포화 세기(0 = 없음) */
  readonly drive?: number;
}
export interface OscLayer extends LayerBase {
  readonly kind: 'osc';
  readonly wave: number;
  readonly freq: Freq;
  /** 위상 FM: 변조 주파수 = 반송 × ratio, 지수 = index × exp(-t/decay) */
  readonly fm?: { readonly ratio: number; readonly index: number; readonly decay: number };
  /** 비브라토(Hz, 센트) */
  readonly vib?: { readonly rate: number; readonly cents: number };
}
export interface NoiseLayer extends LayerBase {
  readonly kind: 'noise';
  readonly color: number;
}
export interface GrainLayer extends LayerBase {
  readonly kind: 'grains';
  /** 핑 개수, 흩어지는 구간(초), 주파수 범위(로그 균등) */
  readonly count: number;
  readonly spread: number;
  readonly lo: number;
  readonly hi: number;
  /** 시간 순으로 주파수를 올린다(반짝이 상승) / 내린다 */
  readonly order?: 1 | -1;
  /** 나중 핑일수록 작게(dB, 구간 끝에서) */
  readonly fall?: number;
  /** 핑 하나: 파형(사인·삼각)과 FM(벨 질감) */
  readonly wave?: number;
  readonly fm?: { readonly ratio: number; readonly index: number; readonly decay: number };
}
export type Layer = OscLayer | NoiseLayer | GrainLayer;

export interface Recipe {
  /** 렌더 길이(초) = 길이 예산 */
  readonly dur: number;
  readonly layers: readonly Layer[];
  /** 되먹임 딜레이(초·되먹임·젖은 양·고리 저역 Hz) */
  readonly delay?: { readonly time: number; readonly fb: number; readonly wet: number; readonly damp: number };
  /** 마스터 포화(피크를 다듬어 창 RMS를 맞추기 쉽게) */
  readonly drive?: number;
  /** 시드 미세 변형: 층별 피치(센트)·시작(초)·이득(dB) 흔들림 폭 */
  readonly vary: { readonly cents: number; readonly time: number; readonly db: number };
}

/** 피크 상한(dBFS). 브리프 상한 -1dBFS에 부동소수·리샘플 여유 0.2dB */
export const PEAK_CEIL_DB = -1.2;
/** 창 RMS 창 길이·이동(초) */
export const RMS_WIN = 0.1;
export const RMS_HOP = 0.025;
const FADE_LAYER = 0.008;
const FADE_END = 0.02;
const TAU = Math.PI * 2;
/** 사인 표(주기 단위 입력, 선형 보간, 오차 1e-7 수준) — 렌더에서 Math.sin을 대신한다 */
const SIN_N = 4096;
const SIN_T = new Float32Array(SIN_N + 1);
for (let i = 0; i <= SIN_N; i++) SIN_T[i] = Math.sin((TAU * i) / SIN_N);
/** sin(2π·x), x는 주기(임의 실수) */
export function sinCyc(x: number): number {
  x -= Math.floor(x);
  const f = x * SIN_N, i = f | 0;
  return SIN_T[i] + (SIN_T[i + 1] - SIN_T[i]) * (f - i);
}
const INV_TAU = 1 / TAU;

/** 시드 xorshift32 → 0..1. 렌더 전용(엔진의 재생 난수는 따로) */
export class Rng {
  private s: number;
  constructor(seed: number) { this.s = (seed | 0) || 0x6d2b79f5; }
  next(): number {
    let s = this.s;
    s ^= s << 13; s ^= s >>> 17; s ^= s << 5;
    this.s = s;
    return (s >>> 0) / 4294967296;
  }
  /** -1..1 */
  bi(): number { return this.next() * 2 - 1; }
}

export const dbToLin = (db: number): number => Math.pow(10, db / 20);
export const linToDb = (v: number): number => (v > 0 ? 20 * Math.log10(v) : -Infinity);

function freqAt(f: Freq, t: number, mul: number): number {
  if (typeof f === 'number') return f * mul;
  const u = f.time > 0 ? Math.min(1, t / f.time) : 1;
  return f.from * Math.pow(f.to / f.from, u) * mul;
}

/** 엔벨로프를 env 배열 [0, n)에 채운다(감쇠·페이드는 곱셈 누적 — 샘플마다 exp를 부르지 않는다) */
function fillEnv(e: Env, n: number, sr: number, env: Float32Array): void {
  const aN = Math.round(e.a * sr), hN = aN + Math.round((e.h ?? 0) * sr);
  const k = Math.exp(-1 / Math.max(1e-4, e.d * sr));
  const curve = e.ac ?? 1;
  let v = 1;
  for (let i = 0; i < n; i++) {
    if (i < aN) { const u = i / aN; env[i] = curve === 1 ? u : Math.pow(u, curve); }
    else if (i < hN) env[i] = 1;
    else { env[i] = v; v *= k; }
  }
  const fN = Math.min(n, Math.round(FADE_LAYER * sr));
  for (let i = 0; i < fN; i++) env[n - 1 - i] *= i / fN;
}

/** 렌더 작업 배열(렌더는 재진입하지 않는다). 가장 긴 층에 맞춰 한 번 자란다 */
let ENV = new Float32Array(0);
function scratch(n: number): void {
  if (ENV.length < n) ENV = new Float32Array(n);
}

/** RBJ 바이쿼드(직접형 I). 스윕은 16샘플마다 계수를 다시 잡는다 */
class Biquad {
  private b0 = 1; private b1 = 0; private b2 = 0; private a1 = 0; private a2 = 0;
  private x1 = 0; private x2 = 0; private y1 = 0; private y2 = 0;
  constructor(private readonly type: number, private readonly sr: number) {}
  set(freq: number, q: number): void {
    const f = Math.min(Math.max(freq, 10), this.sr * 0.45);
    const w = (TAU * f) / this.sr, cw = Math.cos(w), sw = Math.sin(w), alpha = sw / (2 * Math.max(q, 0.05));
    const a0 = 1 + alpha;
    let b0: number, b1: number, b2: number;
    if (this.type === Filt.Lp) { b1 = (1 - cw) / a0; b0 = b1 / 2; b2 = b0; }
    else if (this.type === Filt.Hp) { b1 = -(1 + cw) / a0; b0 = -b1 / 2; b2 = b0; }
    else { b0 = alpha / a0; b1 = 0; b2 = -alpha / a0; }
    this.b0 = b0; this.b1 = b1; this.b2 = b2; this.a1 = (-2 * cw) / a0; this.a2 = (1 - alpha) / a0;
  }
  run(x: number): number {
    const y = this.b0 * x + this.b1 * this.x1 + this.b2 * this.x2 - this.a1 * this.y1 - this.a2 * this.y2;
    this.x2 = this.x1; this.x1 = x; this.y2 = this.y1; this.y1 = y;
    return y;
  }
}

/** 띠 제한 톱니·사각용 PolyBLEP */
function blep(t: number, dt: number): number {
  if (t < dt) { const x = t / dt; return x + x - x * x - 1; }
  if (t > 1 - dt) { const x = (t - 1) / dt; return x * x + x + x + 1; }
  return 0;
}

interface LayerVar { mul: number; shift: number; gain: number; seed: number }

/** osc·noise 층 한 단계에 그리는 최대 샘플 수(32kHz에서 0.1초) — 단계 하나가 수 ms를 넘지 않게 */
const LAYER_CHUNK = 3200;
const fr = Math.fround;

/**
 * osc·noise 층을 샘플 구간으로 나눠 그리는 진행 상태. 발진기 위상·FM·필터·잡음 난수·엔벨로프·스윕을 샘플마다 이어 가므로
 * 몇 번에 나눠 그려도 한 번에 그린 것과 샘플이 같다(중간값은 예전 작업 배열처럼 float32로 반올림).
 */
class LayerRun {
  private L: OscLayer | NoiseLayer | null = null;
  private sr = 0; private start = 0; private n = 0; private i = 0; private g = 0; private mul = 1;
  // 발진기
  private phase = 0; private mphase = 0; private idx = 0; private fk = 1; private vibW = 0; private vibX = 0;
  private fx = 0; private fr_ = 1; private sN = 0;
  // 잡음
  private rng = new Rng(1); private b0 = 0; private b1 = 0; private b2 = 0; private brown = 0;
  // 필터·엔벨로프
  private filt: Biquad | null = null; private fmul = 1; private drive = 0; private td = 1;
  private aN = 0; private hN = 0; private ek = 1; private ev = 1; private curve = 1; private fN = 0;

  /** 층 시작. 그릴 샘플이 없으면 false */
  begin(L: OscLayer | NoiseLayer, out: Float32Array, sr: number, v: LayerVar): boolean {
    this.L = L; this.sr = sr;
    this.start = Math.max(0, Math.round((L.at + v.shift) * sr));
    this.n = Math.min(out.length - this.start, Math.round(L.dur * sr));
    this.i = 0;
    if (this.n <= 0) return false;
    this.g = dbToLin(L.gainDb) * v.gain;
    if (L.kind === 'osc') {
      const fm = L.fm, vib = L.vib, f = L.freq;
      this.fk = fm ? Math.exp(-1 / Math.max(1e-4, fm.decay * sr)) : 1;
      this.phase = 0; this.mphase = 0; this.idx = fm ? fm.index : 0;
      this.vibW = vib ? vib.rate / sr : 0; this.vibX = vib ? (vib.cents / 1200) * Math.LN2 : 0;
      if (typeof f === 'number') { this.fx = f * v.mul; this.sN = 0; this.fr_ = 1; }
      else { this.sN = Math.max(1, Math.round(f.time * sr)); this.fr_ = Math.pow(f.to / f.from, 1 / this.sN); this.fx = f.from * v.mul; }
      this.fmul = 1;
    } else {
      this.rng = new Rng(v.seed); this.b0 = this.b1 = this.b2 = this.brown = 0;
      this.fmul = v.mul;
    }
    this.filt = L.filter ? new Biquad(L.filter.type, sr) : null;
    this.drive = L.drive ?? 0; this.td = this.drive ? Math.tanh(this.drive) : 1;
    const e = L.env;
    this.aN = Math.round(e.a * sr); this.hN = this.aN + Math.round((e.h ?? 0) * sr);
    this.ek = Math.exp(-1 / Math.max(1e-4, e.d * sr)); this.ev = 1; this.curve = e.ac ?? 1;
    this.fN = Math.min(this.n, Math.round(FADE_LAYER * sr));
    return true;
  }

  /** 최대 max 샘플을 그린다. 층이 끝났으면 true */
  run(out: Float32Array, max: number): boolean {
    const L = this.L!, sr = this.sr, n = this.n, end = Math.min(n, this.i + max);
    const filt = this.filt, fspec = L.filter, drive = this.drive;
    for (let i = this.i; i < end; i++) {
      let s: number;
      if (L.kind === 'osc') {
        let f = fr(this.fx);
        if (i < this.sN) this.fx *= this.fr_;
        if (L.vib) { const x = this.vibX * sinCyc(this.vibW * i); f *= 1 + x + 0.5 * x * x; }
        const dt = f / sr, phase = this.phase;
        let y: number;
        if (L.wave === Wave.Sine) {
          let pm = 0;
          const fm = L.fm;
          if (fm) {
            this.mphase += dt * fm.ratio; if (this.mphase >= 1) this.mphase -= Math.floor(this.mphase);
            pm = this.idx * sinCyc(this.mphase); this.idx *= this.fk;
          }
          y = sinCyc(phase + pm * INV_TAU);
        } else if (L.wave === Wave.Tri) y = 1 - 4 * Math.abs(phase - 0.5);
        else if (L.wave === Wave.Saw) y = 2 * phase - 1 - blep(phase, dt);
        else y = (phase < 0.5 ? 1 : -1) + blep(phase, dt) - blep((phase + 0.5) % 1, dt);
        let p = phase + dt; if (p >= 1) p -= Math.floor(p);
        this.phase = p;
        s = fr(y);
      } else {
        const w = this.rng.bi();
        if (L.color === Noise.Pink) {
          // Paul Kellet 간이 분홍 잡음
          this.b0 = 0.99765 * this.b0 + w * 0.099046; this.b1 = 0.963 * this.b1 + w * 0.2965164; this.b2 = 0.57 * this.b2 + w * 1.0526913;
          s = fr((this.b0 + this.b1 + this.b2 + w * 0.1848) * 0.25);
        } else if (L.color === Noise.Brown) { this.brown = (this.brown + 0.02 * w) * 0.998; s = fr(this.brown * 3.5); }
        else s = fr(w);
      }
      if (filt) {
        if ((i & 15) === 0) filt.set(freqAt(fspec!.freq, i / sr, this.fmul), fspec!.q);
        s = fr(filt.run(s));
      }
      // 엔벨로프(선형 어택·유지·곱셈 감쇠, 끝 8ms 페이드)
      let e: number;
      if (i < this.aN) { const u = i / this.aN; e = this.curve === 1 ? u : Math.pow(u, this.curve); }
      else if (i < this.hN) e = 1;
      else { e = this.ev; this.ev *= this.ek; }
      e = fr(e);
      const j = n - 1 - i;
      if (j < this.fN) e = fr(e * (j / this.fN));
      const y = drive ? Math.tanh(s * drive) / this.td : s;
      out[this.start + i] += y * e * this.g;
    }
    this.i = end;
    return end >= n;
  }
}

/** 반짝이 층 진행 상태: 핑 시각·주파수를 먼저 뽑고, 핑을 몇 개씩 나눠 그린다(나눠 그려도 난수 순서가 같아 결과가 같다) */
interface GrainState { L: GrainLayer; v: LayerVar; rng: Rng; times: number[]; freqs: number[]; g: number; n0: number; fk: number; k: number }

function grainsBegin(L: GrainLayer, v: LayerVar, sr: number): GrainState {
  const rng = new Rng(v.seed);
  const times: number[] = [], freqs: number[] = [];
  for (let k = 0; k < L.count; k++) times.push(rng.next() * L.spread);
  for (let k = 0; k < L.count; k++) freqs.push(L.lo * Math.pow(L.hi / L.lo, rng.next()));
  if (L.order) { times.sort((a, b) => a - b); freqs.sort((a, b) => (a - b) * L.order!); }
  const fm = L.fm;
  return { L, v, rng, times, freqs, g: dbToLin(L.gainDb) * v.gain, n0: Math.round(L.dur * sr), fk: fm ? Math.exp(-1 / Math.max(1e-4, fm.decay * sr)) : 1, k: 0 };
}

/** 핑을 최대 max개 그린다. 다 그렸으면 true */
function grainsStep(s: GrainState, out: Float32Array, sr: number, max: number): boolean {
  const L = s.L, fm = L.fm;
  scratch(s.n0);
  fillEnv(L.env, s.n0, sr, ENV);
  const end = Math.min(L.count, s.k + max);
  for (; s.k < end; s.k++) {
    const k = s.k;
    const at = L.at + s.v.shift + s.times[k];
    const fall = L.fall && L.spread > 0 ? dbToLin(-L.fall * (s.times[k] / L.spread)) : 1;
    const start = Math.max(0, Math.round(at * sr));
    const n = Math.min(out.length - start, s.n0);
    const f = s.freqs[k] * s.v.mul, dt = f / sr, gk = s.g * fall * (0.7 + 0.3 * s.rng.next());
    let phase = s.rng.next(), mphase = 0, idx = fm ? fm.index : 0;
    for (let i = 0; i < n; i++) {
      let pm = 0;
      if (fm) { mphase += dt * fm.ratio; if (mphase >= 1) mphase -= 1; pm = idx * sinCyc(mphase); idx *= s.fk; }
      const y = L.wave === Wave.Tri ? 1 - 4 * Math.abs(phase - 0.5) : sinCyc(phase + pm * INV_TAU);
      phase += dt; if (phase >= 1) phase -= 1;
      out[start + i] += y * ENV[i] * gk;
    }
  }
  return s.k >= L.count;
}

/** 창 RMS 최대값(선형) — 짧은 효과음의 체감 음량 기준(꼬리 길이에 덜 흔들린다) */
export function windowRms(x: Float32Array, sr: number, n = x.length): number {
  const win = Math.max(1, Math.round(RMS_WIN * sr)), hop = Math.max(1, Math.round(RMS_HOP * sr));
  let best = 0;
  for (let s = 0; s < n; s += hop) {
    const e = Math.min(n, s + win);
    let sum = 0;
    for (let i = s; i < e; i++) sum += x[i] * x[i];
    const r = Math.sqrt(sum / win);
    if (r > best) best = r;
    if (e === n) break;
  }
  return best;
}

export function peak(x: Float32Array, n = x.length): number {
  let p = 0;
  for (let i = 0; i < n; i++) { const a = x[i] < 0 ? -x[i] : x[i]; if (a > p) p = a; }
  return p;
}

/** 전체 RMS(선형) */
export function fullRms(x: Float32Array, n = x.length): number {
  let s = 0;
  for (let i = 0; i < n; i++) s += x[i] * x[i];
  return n > 0 ? Math.sqrt(s / n) : 0;
}

/** 첫 소리 시각(초): 진폭이 피크 대비 thresholdDb를 처음 넘는 곳 */
export function onset(x: Float32Array, sr: number, thresholdDb = -30): number {
  const th = peak(x) * dbToLin(thresholdDb);
  for (let i = 0; i < x.length; i++) if ((x[i] < 0 ? -x[i] : x[i]) >= th) return i / sr;
  return x.length / sr;
}

/** 반짝이 층을 한 단계에 그리는 핑 수(단계 하나가 수 ms를 넘지 않게) */
const GRAINS_PER_STEP = 4;

/**
 * 레시피 렌더를 작은 단계로 나눈 작업(c019 워밍 예산). 단계 = 층 시작 / osc·noise 층 3200샘플 / 반짝이 핑 4개 / 딜레이 / 음량 맞춤.
 * 엔진은 프레임·유휴 시간 예산 안에서 step을 부르고, 한 번에 끝까지 돌린 renderRecipe와 샘플이 같다.
 */
export class RecipeJob {
  private r: Recipe | null = null;
  private sr = 0;
  private target = 0;
  private buf: Float32Array = new Float32Array(0);
  private rng = new Rng(1);
  private li = 0;
  private grains: GrainState | null = null;
  private readonly layer = new LayerRun();
  private layerOn = false;
  /** 0 층, 1 딜레이, 2 음량 맞춤, 3 끝 */
  private stage = 3;

  get done(): boolean { return this.stage >= 3; }
  /** 결과(done 뒤에 읽는다) */
  get output(): Float32Array { return this.buf; }

  begin(r: Recipe, sr: number, seed: number, targetDb: number, out?: Float32Array): void {
    const len = Math.max(1, Math.round(r.dur * sr));
    this.buf = out && out.length >= len ? out.subarray(0, len) : new Float32Array(len);
    this.buf.fill(0);
    this.r = r; this.sr = sr; this.target = targetDb;
    this.rng = new Rng((seed ^ 0x2545f491) >>> 0);
    this.li = 0; this.grains = null; this.layerOn = false; this.stage = 0;
  }

  /** 단계 하나. 끝났으면 true */
  step(): boolean {
    const r = this.r!, sr = this.sr, buf = this.buf, len = buf.length;
    if (this.stage === 0) {
      if (this.grains) {
        if (grainsStep(this.grains, buf, sr, GRAINS_PER_STEP)) { this.grains = null; this.li++; }
      } else if (this.layerOn) {
        if (this.layer.run(buf, LAYER_CHUNK)) { this.layerOn = false; this.li++; }
      } else if (this.li < r.layers.length) {
        const li = this.li, L = r.layers[li], rng = this.rng;
        const v: LayerVar = {
          mul: Math.pow(2, (r.vary.cents * rng.bi()) / 1200),
          // 첫 층(대개 타격 순간)은 시작을 흔들지 않는다 — 텔레그래프·타격의 첫 소리가 늦어지지 않게
          shift: li === 0 ? 0 : Math.max(-L.at, r.vary.time * rng.bi()),
          gain: dbToLin(r.vary.db * rng.bi()),
          seed: (Math.floor(rng.next() * 0x7fffffff) ^ (li * 0x9e3779b1)) | 0,
        };
        if (L.kind === 'grains') this.grains = grainsBegin(L, v, sr);
        else if (this.layer.begin(L, buf, sr, v)) this.layerOn = true;
        else this.li++;
      }
      if (!this.grains && !this.layerOn && this.li >= r.layers.length) this.stage = 1;
      return false;
    }
    if (this.stage === 1) {
      if (r.delay) {
        const D = r.delay, dl = new Float32Array(Math.max(1, Math.round(D.time * sr)));
        const k = 1 - Math.exp((-TAU * D.damp) / sr);
        let idx = 0, lp = 0;
        for (let i = 0; i < len; i++) {
          const d = dl[idx];
          lp += k * (d - lp);
          dl[idx] = buf[i] + D.fb * lp;
          idx = idx + 1 === dl.length ? 0 : idx + 1;
          buf[i] += D.wet * lp;
        }
      }
      this.stage = 2;
      return false;
    }
    if (this.stage === 2) {
      // 1차 맞춤 → 포화 → 끝 페이드 → 최종 맞춤(포화가 피크를 깎은 만큼 RMS를 다시 올린다)
      const targetDb = this.target;
      const pre = windowRms(buf, sr);
      if (pre > 0) { const g = dbToLin(targetDb) / pre; for (let i = 0; i < len; i++) buf[i] *= g; }
      if (r.drive) for (let i = 0; i < len; i++) buf[i] = Math.tanh(buf[i] * r.drive) / r.drive;
      const fade = Math.min(len, Math.round(FADE_END * sr));
      for (let i = 0; i < fade; i++) buf[len - 1 - i] *= i / fade;
      const rms = windowRms(buf, sr), pk = peak(buf);
      if (rms > 0) {
        const g = Math.min(dbToLin(targetDb) / rms, dbToLin(PEAK_CEIL_DB) / pk);
        for (let i = 0; i < len; i++) buf[i] *= g;
      }
      this.stage = 3;
    }
    return this.stage >= 3;
  }
}

/**
 * 레시피 하나를 렌더한다. targetDb = 창 RMS 목표(dBFS). 결과는 피크 PEAK_CEIL_DB 이하.
 * out을 주면 그 배열(길이 ≥ dur×sr)에 쓰고 쓴 길이만큼의 뷰를 돌려준다. RecipeJob을 끝까지 돌린 것과 같다.
 */
export function renderRecipe(r: Recipe, sr: number, seed: number, targetDb: number, out?: Float32Array): Float32Array {
  const job = new RecipeJob();
  job.begin(r, sr, seed, targetDb, out);
  while (!job.step()) { /* 끝까지 */ }
  return job.output;
}
